import { RegionsCode } from '../../constants/region';
import { UserId, Uuid } from '../../models/Base';
import { RepositoryElement } from '../../models/Repository';
import { deepTransformDate, deepTransformTimestamp } from '../../utils/date';

export enum AlbumRankingType {
  Individual = 'individual',
  Team = 'team',
  Region = 'region',
}

export enum AlbumRankTier {
  Band1 = 'band1',
  Band2 = 'band2',
  Band3 = 'band3',
  First = 'first',
  Second = 'second',
  Third = 'third',
  Top5 = 'top5',
  Top10 = 'top10',
  Top50 = 'top50',
  Top100 = 'top100',
  Others = 'others',
}

export type AlbumBand =
  | AlbumRankTier.Band1
  | AlbumRankTier.Band2
  | AlbumRankTier.Band3
  | AlbumRankTier.Others;

export enum AlbumRankingState {
  Active = 'active',
  Inactive = 'inactive',
  ActiveProvisional = 'active&provisional',
  InactiveProvisional = 'inactive&provisional',
  Final = 'final',
  NotApplicable = 'notApplicable',
}

const getBand = (
  score: number | undefined,
  totalScore: number | undefined,
): AlbumBand => {
  if (totalScore === undefined || totalScore === 0) {
    return AlbumRankTier.Others;
  }

  const ratio = (score ?? 0) / totalScore;
  if (ratio >= 0.95) {
    return AlbumRankTier.Band1;
  } else if (ratio >= 0.75) {
    return AlbumRankTier.Band2;
  } else if (ratio >= 0.5) {
    return AlbumRankTier.Band3;
  } else {
    return AlbumRankTier.Others;
  }
};

export type UserRankingJson<T = number> = {
  leaderboardId: Uuid;
  classroomId: Uuid;
  userId: UserId;
  lasModifiedAt: T;
  state: AlbumRankingState;
  region: RegionsCode;
  teamId: string | null;
  score?: number;
  totalDurationInMs?: number;
  achievedAt?: T;
  lastNotificationSentAt?: T;
  rankNumber?: number | null;
  teamRankNumber?: number | null;
  regionRankNumber?: number | null;
  rankTier?: AlbumRankTier;
  finalizedAt?: T;
};

export class AlbumUserRanking
  extends RepositoryElement
  implements UserRankingJson<Date>
{
  readonly leaderboardId!: Uuid;
  classroomId!: Uuid;
  userId!: UserId;
  lasModifiedAt!: Date;
  state!: AlbumRankingState;
  region!: RegionsCode;
  teamId!: string | null;
  score?: number;
  totalDurationInMs?: number;
  achievedAt?: Date;
  lastNotificationSentAt?: Date;
  rankNumber?: number | null;
  teamRankNumber?: number | null;
  regionRankNumber?: number | null;
  rankTier?: AlbumRankTier;
  finalizedAt?: Date;

  constructor(albumUserRanking: UserRankingJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(albumUserRanking));
  }

  id(): string {
    return `${this.classroomId}_${this.leaderboardId}_${this.userId}`;
  }

  toJSON(): UserRankingJson {
    return deepTransformDate(this) as unknown as UserRankingJson;
  }

  getBand(totalScore: number | undefined) {
    return getBand(this.score, totalScore);
  }
}

// TeamRank
// {
//     leaderboardId,
//     classroomId,
//     teamId,
//     lastModifiedAt,
//     state,
//     score,
//     totalDurationInMs,
//     achievedAt,
//     rankNumber,
//     rankTier,
//     finalizedAt,
// }

export type RegionRankingJson<T = number> = {
  leaderboardId: Uuid;
  classroomId: Uuid;
  region: RegionsCode;
  lasModifiedAt: T;
  state: AlbumRankingState;
  score?: number;
  totalDurationInMs?: number;
  achievedAt?: T;
  rankNumber?: number | null;
  rankTier?: AlbumRankTier;
  finalizedAt?: T;
};

export class AlbumRegionRanking
  extends RepositoryElement
  implements RegionRankingJson<Date>
{
  readonly leaderboardId!: Uuid;
  classroomId!: Uuid;
  region!: RegionsCode;
  lasModifiedAt!: Date;
  state!: AlbumRankingState;
  score?: number;
  totalDurationInMs?: number;
  achievedAt?: Date;
  rankNumber?: number | null;
  rankTier?: AlbumRankTier;
  finalizedAt?: Date;

  constructor(albumRegionRanking: RegionRankingJson<Date | number>) {
    super();
    Object.assign(this, deepTransformTimestamp(albumRegionRanking));
  }

  id(): string {
    return `${this.classroomId}_${this.leaderboardId}_${this.region}`;
  }

  toJSON(): RegionRankingJson {
    return deepTransformDate(this) as unknown as RegionRankingJson;
  }

  getBand(totalScore: number | undefined): AlbumRankTier {
    return getBand(this.score, totalScore);
  }
}
